<template>
  <span
    class="view-date"
    :id="field.key"
    :title="useBankPaymentDatesList ? undefined : fieldTooltipTitle"
  >
    <!-- Duntower: todas las fechas de pagos marcados pagados (con tooltip por pago) -->
    <template v-if="useBankPaymentDatesList">
      <span v-if="bankPaymentDatesLoading" class="text-muted">…</span>
      <template v-else-if="bankPaymentDateEntries.length">
        <span
          v-for="(entry, index) in bankPaymentDateEntries"
          :key="entry.id + '-' + entry.ymd + '-' + index"
          class="date-item bank-payment-date-entry"
          :title="entry.tooltip"
        >
          {{ entry.label }}
          <span v-if="index < bankPaymentDateEntries.length - 1">, </span>
        </span>
      </template>
      <span v-else class="text-muted">—</span>
    </template>
    <!-- Si es array, mostrar todas las fechas -->
    <span v-else-if="field.isArray && Array.isArray(displayValue)">
      <span
        v-for="(dateItem, index) in displayValue"
        :key="index"
        class="date-item"
      >
        {{ formatDateForDisplay(dateItem) }}
        <span v-if="index < displayValue.length - 1">, </span>
      </span>
    </span>
    <!-- Valor simple -->
    <span v-else>
      {{ formatDateForDisplay(displayValue) }}
    </span>
  </span>
</template>

<script>
import { formatDateSync, formatDateTimeSync } from "@/utils/dateFormatter";
import { wencesApi } from "@/api/wencesApi";
import { isDuntowerTenant } from "@/utils/dynamicModelValidationSwal";

export default {
  name: "ViewFieldDate",
  props: {
    field: {
      type: Object,
      required: true,
    },
    value: {
      type: [String, Date, Array],
      default: null,
    },
    item: {
      type: Object,
      default: null,
    },
    modelName: {
      type: String,
      default: "",
    },
    isGroupModel: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      bankPaymentDatesLoading: false,
      bankPaymentDateEntries: [],
      bankPaymentDatesLoadKey: "",
    };
  },
  computed: {
    displayValue() {
      if (this.value !== null && this.value !== undefined && this.value !== "") {
        return this.value;
      }
      if (this.field.defaultValue !== undefined && this.field.defaultValue !== null) {
        return this.field.defaultValue;
      }
      return null;
    },
    fieldTooltipTitle() {
      const tip = this.field && this.field.tooltip;
      if (tip == null || String(tip).trim() === "") return undefined;
      return String(tip);
    },
    /** Columna Bank payment date en gastos de embarque (Duntower). */
    useBankPaymentDatesList() {
      if (!isDuntowerTenant()) return false;
      if (!this.field || this.field.key !== "payment_date") return false;
      const payments = this.item && this.item.payments;
      return Array.isArray(payments) && payments.length > 0;
    },
    paymentRefsKey() {
      if (!this.useBankPaymentDatesList) return "";
      const payments = this.item.payments || [];
      return payments
        .map((p) => this.getPaymentId(p) || "")
        .filter(Boolean)
        .join(",");
    },
  },
  watch: {
    paymentRefsKey: {
      immediate: true,
      handler(key) {
        if (!this.useBankPaymentDatesList) {
          this.bankPaymentDateEntries = [];
          this.bankPaymentDatesLoading = false;
          this.bankPaymentDatesLoadKey = "";
          return;
        }
        if (key === this.bankPaymentDatesLoadKey && this.bankPaymentDateEntries.length) {
          return;
        }
        this.loadBankPaymentDates();
      },
    },
  },
  methods: {
    formatDateForDisplay(value) {
      if (!value) return "";
      return this.field.time
        ? formatDateTimeSync(value, "")
        : formatDateSync(value, "");
    },
    getPaymentId(ref) {
      if (ref == null || ref === "") return null;
      if (typeof ref === "object" && ref._id != null) return String(ref._id);
      return String(ref);
    },
    normalizePaidFlag(p) {
      if (p === true || p === "true" || p === 1 || p === "1") return true;
      if (p === false || p === "false" || p === 0 || p === "0") return false;
      return null;
    },
    ymdFromPayment(payment) {
      if (!payment) return "";
      const raw = payment.payment_date != null && payment.payment_date !== ""
        ? payment.payment_date
        : payment.date;
      if (raw == null || raw === "") return "";
      if (typeof raw === "string") {
        const m = raw.match(/^(\d{4}-\d{2}-\d{2})/);
        if (m) return m[1];
      }
      if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
        return raw.toISOString().slice(0, 10);
      }
      const parsed = new Date(raw);
      if (Number.isNaN(parsed.getTime())) return "";
      return parsed.toISOString().slice(0, 10);
    },
    calendarCompareMs(ymd) {
      if (!ymd) return Number.NEGATIVE_INFINITY;
      return Date.parse(`${ymd}T12:00:00.000Z`);
    },
    paymentDisplayName(payment) {
      if (!payment) return "";
      const name = payment.name != null ? String(payment.name).trim() : "";
      if (name) return name;
      const label = payment.label != null ? String(payment.label).trim() : "";
      if (label) return label;
      return payment._id ? String(payment._id) : "";
    },
    async resolvePaymentDoc(ref) {
      if (ref && typeof ref === "object" && ref._id != null) {
        const hasPaid = Object.prototype.hasOwnProperty.call(ref, "paid");
        const hasDate =
          (ref.payment_date != null && ref.payment_date !== "") ||
          (ref.date != null && ref.date !== "");
        if (hasPaid && hasDate) {
          return ref;
        }
      }
      const id = this.getPaymentId(ref);
      if (!id) return null;
      try {
        const { data } = await wencesApi.get(`/api/gotrading/dynamic/payment/${id}`);
        return (data && data.data) || data || null;
      } catch (e) {
        console.error("[ViewFieldDate] load payment for bank dates:", id, e);
        return null;
      }
    },
    async loadBankPaymentDates() {
      if (!this.useBankPaymentDatesList) {
        this.bankPaymentDateEntries = [];
        return;
      }
      const loadKey = this.paymentRefsKey;
      this.bankPaymentDatesLoading = true;
      this.bankPaymentDatesLoadKey = loadKey;
      try {
        const payments = this.item.payments || [];
        const resolved = await Promise.all(payments.map((p) => this.resolvePaymentDoc(p)));
        if (loadKey !== this.bankPaymentDatesLoadKey) return;

        const entries = [];
        for (const payment of resolved) {
          if (!payment || payment.deleted === true) continue;
          if (this.normalizePaidFlag(payment.paid) !== true) continue;
          const ymd = this.ymdFromPayment(payment);
          if (!ymd) continue;
          const name = this.paymentDisplayName(payment);
          entries.push({
            id: payment._id != null ? String(payment._id) : ymd,
            ymd,
            label: this.formatDateForDisplay(ymd),
            tooltip: name
              ? this.$t("financialSummary.payments.bankPaymentDateForPaymentHint", {
                  name,
                })
              : this.$t("financialSummary.payments.paymentLinkPaidHint"),
            sortMs: this.calendarCompareMs(ymd),
          });
        }
        entries.sort((a, b) => a.sortMs - b.sortMs);
        this.bankPaymentDateEntries = entries;
      } finally {
        if (loadKey === this.bankPaymentDatesLoadKey) {
          this.bankPaymentDatesLoading = false;
        }
      }
    },
  },
};
</script>

<style scoped>
.view-date {
  font-weight: 500;
}

.date-item {
  display: inline;
}

.bank-payment-date-entry {
  cursor: help;
  border-bottom: 1px dotted rgba(0, 0, 0, 0.35);
}
</style>
